import { Button, Kind, Size } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import ExternalLink from 'components/Icons/ExternalLink'
import { providerLogo } from 'components/ProviderLogo/providerLogos'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { merchantTheme } from 'theme'
import {
  CompatibilityRow,
  CompatibilityState,
  LIVE_GREEN,
  NEEDS_ACTIVATION_GOLD,
  NEEDS_ACTIVATION_GOLD_TEXT,
} from '../compatibility'
import { ExclamationCircleIcon } from '../icons'

const t = buildTranslate('promo_codes.overview.where_it_works')
const tOverview = buildTranslate('promo_codes.overview')

// Figma Inspect values for the compatibility rows. These status tints have no
// equivalent in constants/palette.ts. LIVE_GREEN / NEEDS_ACTIVATION_GOLD are
// shared with ChannelStatusCell, so they're imported from ../compatibility; the
// two below are specific to this card.
const NEEDS_ACTIVATION_TINT = '#FDF8E8'
const NOT_SUPPORTED_DOT = '#E0E3E6'
// Figma's Grey-100. It exists in stylesheets/variables/_palette.scss but is not
// re-exported by constants/palette.ts, and it is not the same as grey90.
const NOTICE_TEXT = '#363436'

type StateStyle = {
  rowBackground: string
  pillBackground: string
  text: string
  dot: string
}

const STATE_STYLES: Record<CompatibilityState, StateStyle> = {
  live: {
    rowBackground: palette.white,
    pillBackground: palette.nephritis10,
    text: LIVE_GREEN,
    dot: palette.nephritis,
  },
  // Grey treatment mirrors ChannelStatusCell's `pos_coming_soon` so Toast reads
  // the same "coming soon" state on both promo-code surfaces. The label is
  // normal-size text (WCAG AA 4.5:1 bar) so it uses grey90; the dot is a
  // graphical object (3:1 bar) and keeps the lighter grey70.
  coming_soon: {
    rowBackground: palette.white,
    pillBackground: merchantTheme.colors.grey05,
    text: palette.grey90,
    dot: palette.grey70,
  },
  needs_activation: {
    rowBackground: NEEDS_ACTIVATION_TINT,
    pillBackground: 'transparent',
    // AA-compliant gold for the pill text (4.5:1); the dot keeps the brand gold
    // (graphical, 3:1). See NEEDS_ACTIVATION_GOLD_TEXT in ../compatibility.
    text: NEEDS_ACTIVATION_GOLD_TEXT,
    dot: NEEDS_ACTIVATION_GOLD,
  },
  not_supported: {
    rowBackground: palette.white,
    pillBackground: merchantTheme.colors.grey05,
    text: palette.grey40,
    dot: NOT_SUPPORTED_DOT,
  },
}

const DOT_SIZE = 7
const LOGO_SIZE = 36
const HEADER_ICON_SIZE = 15
// Gap between the header icon and title; the description is indented by
// icon-width + this gap so it aligns under the title rather than the icon.
const HEADER_ICON_GAP = 8
const HEADER_TEXT_INDENT = HEADER_ICON_SIZE + HEADER_ICON_GAP
const NOTICE_ICON_SIZE = 19
const ROW_PADDING = '16px 24px'
const NOTICE_PADDING = '8px 8px 8px 25px'
const NOTICE_LINE_HEIGHT = '19.2px'
const PILL_HEIGHT = 21

const ProviderLogo = ({ logoKey }: { logoKey: string | null }) => {
  const [css] = useStyletron()
  const src = providerLogo(logoKey)
  if (!src) return null

  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      className={css({
        width: `${LOGO_SIZE}px`,
        height: `${LOGO_SIZE}px`,
        objectFit: 'contain',
      })}
    />
  )
}

type Props = {
  title: string
  description: string
  emptyText: string
  rows: CompatibilityRow[]
  Icon: (props: { size?: number; color?: string }) => JSX.Element
  onActivate?: (row: CompatibilityRow) => void
}

const CompatibilityCard = ({
  title,
  description,
  emptyText,
  rows,
  Icon,
  onActivate,
}: Props) => {
  const [css] = useStyletron()
  const pendingRow = rows.find(row => row.state === 'needs_activation')

  return (
    <div
      className={css({
        backgroundColor: palette.white,
        border: `1px solid ${palette.grey30}`,
        borderRadius: '8px',
        overflow: 'hidden',
      })}
    >
      <div
        className={`grey-05-bkg px-m py-s ${css({
          borderBottom: `1px solid ${palette.grey30}`,
        })}`}
      >
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: `${HEADER_ICON_GAP}px`,
          })}
        >
          <Icon size={HEADER_ICON_SIZE} />
          <Text.SmallCaps3 bold color="grey90" tag="span">
            {title}
          </Text.SmallCaps3>
        </div>
        <Text.BodyText4
          color="grey70"
          tag="div"
          className={css({ paddingLeft: `${HEADER_TEXT_INDENT}px` })}
        >
          {description}
        </Text.BodyText4>
      </div>

      {pendingRow && (
        <div
          data-testid="compatibility-activation-notice"
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: NOTICE_PADDING,
            borderRadius: '4px',
            backgroundColor: palette.eggYolk10,
          })}
        >
          <span
            className={css({
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              height: NOTICE_LINE_HEIGHT,
            })}
          >
            <ExclamationCircleIcon
              size={NOTICE_ICON_SIZE}
              color={NEEDS_ACTIVATION_GOLD}
            />
          </span>
          <Text.BodyText4
            tag="p"
            className={`mb-0 ${css({
              color: NOTICE_TEXT,
              lineHeight: NOTICE_LINE_HEIGHT,
            })}`}
          >
            {t('needs_activation_notice', { provider: pendingRow.label })}
          </Text.BodyText4>
        </div>
      )}

      {rows.length === 0 ? (
        <div
          className={`px-m py-s ${css({
            borderTop: `1px solid ${palette.grey20}`,
          })}`}
        >
          <Text.BodyText4 color="grey70" tag="div">
            {emptyText}
          </Text.BodyText4>
        </div>
      ) : (
        <ul
          role="list"
          className={css({ listStyle: 'none', padding: 0, margin: 0 })}
        >
          {rows.map(row => (
            <li
              key={row.key}
              data-testid={`compatibility-row-${row.key}`}
              className={css({
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: ROW_PADDING,
                borderTop: `1px solid ${palette.grey10}`,
                backgroundColor: STATE_STYLES[row.state].rowBackground,
              })}
            >
              <ProviderLogo logoKey={row.logoKey} />
              <Text.BodyText4
                color={row.state === 'not_supported' ? 'grey50' : 'grey90'}
                tag="span"
              >
                {row.label}
              </Text.BodyText4>
              <span
                className={css({
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  height: `${PILL_HEIGHT}px`,
                  padding: '0 8px',
                  borderRadius: `${PILL_HEIGHT / 2}px`,
                  backgroundColor: STATE_STYLES[row.state].pillBackground,
                })}
              >
                <span
                  aria-hidden="true"
                  className={css({
                    width: `${DOT_SIZE}px`,
                    height: `${DOT_SIZE}px`,
                    borderRadius: '50%',
                    backgroundColor: STATE_STYLES[row.state].dot,
                  })}
                />
                <span
                  className={css({
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    color: STATE_STYLES[row.state].text,
                  })}
                >
                  {t(`states.${row.state}`)}
                </span>
              </span>

              <span className={css({ marginLeft: 'auto' })}>
                {row.state === 'needs_activation' && onActivate && (
                  <Button
                    size={Size.SMALL}
                    onClick={() => onActivate(row)}
                    aria-label={t('activate_aria', { provider: row.label })}
                    data-testid={`activate-${row.key}`}
                  >
                    {t('activate')}
                  </Button>
                )}
                {row.state === 'live' && row.walkthroughUrl && (
                  <Button
                    kind={Kind.MINIMAL}
                    size={Size.SMALL}
                    aria-label={`${t('cashier_walkthrough_aria', {
                      provider: row.label,
                    })} ${tOverview('opens_in_new_window')}`}
                    endEnhancer={() => <ExternalLink />}
                    onClick={() =>
                      window.open(row.walkthroughUrl, '_blank', 'noopener')
                    }
                  >
                    {t('cashier_walkthrough')}
                  </Button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default CompatibilityCard
